/**
 * The Zoho Books import as nine steps (piece 31; decisions 1760, 8040): each step's
 * done test and whether it can be reached, read from the preset's own answer.
 * 🔒 Nothing here is stored; every fact is a run, the ledger or an audited mark.
 */
import type { ImportBlockValue, ZohoBooksPresetView } from './importRun';
import type { StringKey } from '../strings';

export const ZOHO_IMPORT_STEPS = ['connect', 'accountsAndTaxes', 'contacts', 'items', 'documents', 'bankTransactions', 'compare', 'signOff'] as const;
export type ZohoImportStep = (typeof ZOHO_IMPORT_STEPS)[number];

/** The step each block's controls stand inside. */
export const IMPORT_STEP_OF_BLOCK: Readonly<Record<ImportBlockValue, ZohoImportStep>> = {
  AccountsAndTaxes: 'accountsAndTaxes',
  Contacts: 'contacts',
  Items: 'items',
  Documents: 'documents',
  BankTransactions: 'bankTransactions',
  // 🔒 Decision 8380: the CRM's block stands on its own preset's two steps, and the Books steps never offer it; read as the contacts it is read as.
  Manufacturers: 'contacts',
  // 🔒 Decision 12720: the expense reports stand on their own preset, and the Books steps never offer them; read as the documents they link to.
  ExpenseReports: 'documents',
  // 🔒 Decision 9280: the CSV blocks stand on their own preset, and the Books steps never offer them.
  CsvCompanies: 'contacts',
  CsvPeople: 'contacts',
  CsvLocations: 'contacts',
  CsvItems: 'items',
  // 🔒 Decision 12660: the lookup's block stands on the Items list, and the Books steps never offer it.
  ItemLookup: 'items',
};

export interface ZohoImportStepFacts {
  done: boolean;
  /** Whether what the step needs is done; a step that cannot be reached draws as later. */
  reachable: boolean;
  /**
   * 🔒 What a step that cannot be reached waits for, as the sentence its card
   * draws (decision 1970); null while it can be reached.
   */
  waitsFor: StringKey | null;
}

/** 🔒 The block each block's step waits for, as the sentence its Read already carries (decisions 1760, 1970). */
const NEEDS_OF_BLOCK: Readonly<Record<ImportBlockValue, StringKey>> = {
  AccountsAndTaxes: 'import.block.needsAccounts',
  Contacts: 'import.block.needsAccounts',
  Items: 'import.block.needsAccounts',
  Documents: 'import.block.needsContactsAndItems',
  BankTransactions: 'import.block.needsDocuments',
  // 🔒 Decision 8380: Manufacturers waits on no block, so its sentence is never drawn.
  Manufacturers: 'import.block.needsAccounts',
  // 🔒 Decision 12720: the expense reports wait on a documents range, as the bank lines do.
  ExpenseReports: 'import.expense.needsDocuments',
  // 🔒 Decision 9280: the CSV blocks wait on no block, so their sentence is never drawn.
  CsvCompanies: 'import.block.needsAccounts',
  CsvPeople: 'import.block.needsAccounts',
  CsvLocations: 'import.block.needsAccounts',
  CsvItems: 'import.block.needsAccounts',
  ItemLookup: 'import.block.needsAccounts',
};

type StepView = Pick<ZohoBooksPresetView, 'source' | 'blocks' | 'running' | 'staged' | 'documents' | 'uncategorized'>;

/**
 * 🔒 Each step's done test (decision 1760). A block step is done once a
 * finished run read the whole block, and never while a read of it runs or
 * waits. The documents step is done once the ledger is open and written
 * through a day; Compare once the range last written is marked checked; Sign
 * off once the latest Uncategorized report is acknowledged and no Items read
 * runs or waits (decision 2000). A key the answer lacks reads
 * as not done. A step that cannot be reached names what it waits for: the
 * connection rows, the block it needs, a written range, or the comparison
 * (decision 1970).
 */
export function zohoImportSteps(view: StepView): Record<ZohoImportStep, ZohoImportStepFacts> {
  const connected = view.source !== 'none';
  const row = (block: ImportBlockValue) => view.blocks.find((one) => one.block === block) ?? null;
  const held = (block: ImportBlockValue) => view.running?.block === block || view.staged.some((one) => one.block === block);
  const reach = (block: ImportBlockValue): Pick<ZohoImportStepFacts, 'reachable' | 'waitsFor'> => {
    const met = row(block)?.dependencyMet ?? false;
    return { reachable: connected && met, waitsFor: !connected ? 'import.connection.needsRows' : met ? null : NEEDS_OF_BLOCK[block] };
  };
  const wholeBlock = (block: ImportBlockValue): ZohoImportStepFacts => ({
    done: Boolean(row(block)?.writtenWholeAt) && !held(block),
    ...reach(block),
  });
  const range = view.documents.lastPulledRange ?? null;
  const compared = view.documents.compared ?? null;
  const compareDone = range !== null && compared !== null && compared.from === range.from && compared.to === range.to;
  return {
    connect: { done: connected, reachable: true, waitsFor: null },
    accountsAndTaxes: wholeBlock('AccountsAndTaxes'),
    contacts: wholeBlock('Contacts'),
    items: wholeBlock('Items'),
    documents: {
      done: view.documents.goLive !== null && view.documents.pulledThrough !== null && !held('Documents'),
      ...reach('Documents'),
    },
    // 🔒 Decision 8040: Bank transactions is done once a run of the block is written and finished, and none reads or waits.
    bankTransactions: {
      done: row('BankTransactions')?.lastRun?.status === 'Finished' && !held('BankTransactions'),
      ...reach('BankTransactions'),
    },
    compare: { done: compareDone, reachable: range !== null, waitsFor: range === null ? 'import.compare.none' : null },
    signOff: {
      done: compareDone && (view.uncategorized?.acknowledged ?? null) !== null && !held('Items'),
      reachable: compareDone,
      waitsFor: compareDone ? null : 'import.steps.signOff.waits',
    },
  };
}

/**
 * 🔒 The three passes of a person's Contacts read (decision 7980; T3184): the
 * company joins first, then the people, then the write. A read opened before
 * the decision, and a daily pull's, carry no passes and keep the one walk.
 */
export const IMPORT_PASSES = ['Companies', 'People', 'Write'] as const;
export type ImportPass = (typeof IMPORT_PASSES)[number];

/** 🔒 What a pass waits on (decision 7980): the pairs nobody answered, the people questions nobody answered, and the rows left to write. */
export type ImportPassLeft = Readonly<Record<ImportPass, number>>;

/** 🔒 A three-pass read as the block row draws it (decision 7980): the pass it is in and what each pass has left. */
export interface ImportPassesSummary {
  pass: ImportPass;
  left: ImportPassLeft;
}

/**
 * 🔒 The pass a three-pass read is in (decision 7980). The Companies pass holds
 * while any pair waits, since Continue answers every one it leaves. The People
 * pass holds while a question waits, until its Continue. Then the Write pass.
 */
export function importPassOf(input: { companiesLeft: number; peopleLeft: number; peopleContinued: boolean }): ImportPass {
  if (input.companiesLeft > 0) return 'Companies';
  if (input.peopleLeft > 0 && !input.peopleContinued) return 'People';
  return 'Write';
}
